import { useTranslation } from "react-i18next";
import { Content } from "../../commons/Template/Template";
import { useTreasury } from "../Common/TreasuryContext";
import { Fragment, useState } from "react";
import {
  Table,
  TableBody,
  TableHeader,
  TableNestingWrapper,
  TableRow,
  TableRowHeader,
  TableRowItem,
  TableWrapper,
} from "../../commons/Table/Table";
import Button from "../../commons/Button/Button";
import { ExpandLess, ExpandMore } from "@mui/icons-material";
import { formatAmount, formatDate } from "../../../format";
import { i18n } from "../../../i18n";
import { capitalize } from "../../../utils";
import { useAuth } from "../../../auth/AuthContext";
import { FormLoader } from "../../commons/Form/Form";

export default function Debt() {
  const { t } = useTranslation(["common", "treasury"]);
  document.title = t("treasury:debt.title");

  const { loading, monthlyCashFlows, loanTransactions, accounts } =
    useTreasury();

  const { activeCompany, loadingActiveCompany } = useAuth();

  const [selectedRowIndex, setSelectedRowIndex] = useState("");
  const handleSelectRow = (newRow) => {
    setSelectedRowIndex((prevRow) => (prevRow === newRow ? "" : newRow));
  };

  const computePercentage = (val1, val2) =>
    `${((val1 / val2) * 100).toFixed(0)} %`;

  const sortByAscendingDate = (a, b) => (a.date > b.date ? 1 : -1);

  const isFlowNotEmpty = (flow) => flow.loans_in || flow.loans_out;

  const convertFlowIntoRow = (flow, flowIndex) => {
    const borrowedItem = {
      label: t("common:borrowed"),
      value: (
        <ValueContainer>
          <div>
            {formatAmount(
              flow.loans_in,
              i18n.resolvedLanguage,
              activeCompany.currency,
            )}
          </div>
          {flow.loans_in && flow.cash_in ? (
            <Pill>
              {computePercentage(flow.loans_in, flow.cash_in) +
                " " +
                t("treasury:debt.ofIncomes")}
            </Pill>
          ) : null}
        </ValueContainer>
      ),
      formattedValue: formatAmount(
        flow.loans_in,
        i18n.resolvedLanguage,
        activeCompany.currency,
      ),
    };

    const refundedItem = {
      label: t("common:refunded"),
      value: (
        <ValueContainer>
          <div>
            {formatAmount(
              flow.loans_out,
              i18n.resolvedLanguage,
              activeCompany.currency,
            )}
          </div>
          {flow.loans_out && flow.cash_in ? (
            <Pill>
              {computePercentage(flow.loans_out, flow.cash_in) +
                " " +
                t("treasury:debt.ofIncomes")}
            </Pill>
          ) : null}
        </ValueContainer>
      ),
      formattedValue: formatAmount(
        flow.loans_out,
        i18n.resolvedLanguage,
        activeCompany.currency,
      ),
    };

    const buttonItem = {
      value: isFlowNotEmpty(flow) ? (
        <Button
          className="rowButton"
          icon={
            selectedRowIndex === flowIndex ? <ExpandLess /> : <ExpandMore />
          }
        />
      ) : null,
    };
    const row = {
      thValue: capitalize(
        formatDate(flow.date, i18n.resolvedLanguage, "year-month"),
      ),
      onClick: isFlowNotEmpty(flow) ? () => handleSelectRow(flowIndex) : null,
      items: [borrowedItem, refundedItem, buttonItem],
      nestedTable: {
        th: [
          {
            value: t("common:label"),
            style: { width: "50%" },
          },
          {
            value: t("common:funder"),
          },

          {
            value: t("common:amount"),
            style: { textAlign: "right" },
          },
          {
            value: capitalize(t("common:date")),
            style: { textAlign: "right" },
          },
        ],
        tr:
          loanTransactions &&
          loanTransactions
            .filter(
              (tr) => flow.date.substring(0, 7) === tr.date.substring(0, 7),
            )
            .sort(sortByAscendingDate)
            .map((transaction) => ({
              onClick: () => {},
              td: [
                {
                  value: transaction.wording,
                  style: { width: "50%" },
                },
                {
                  value: transaction.financer,
                },

                {
                  value: formatAmount(
                    transaction.value,
                    i18n.resolvedLanguage,
                    accounts.find(
                      (account) => account.id === transaction.account,
                    ).currency,
                  ),
                  style: { textAlign: "right" },
                },
                {
                  value: formatDate(transaction.date, i18n.resolvedLanguage),
                  style: { textAlign: "right" },
                },
              ],
              rawTd: [transaction.value],
            })),
      },
    };
    return row;
  };

  const sortByDescendingDate = (a, b) => (a.date < b.date ? 1 : -1);

  const rows =
    !loading &&
    !loadingActiveCompany &&
    monthlyCashFlows &&
    accounts &&
    loanTransactions &&
    Object.entries(monthlyCashFlows)
      .map(([date, flow]) => ({
        ...flow,
        date: date,
      }))
      .sort(sortByDescendingDate)
      .map(convertFlowIntoRow);

  if (loading || loadingActiveCompany) return <FormLoader />;
  return (
    <Content>
      <TableWrapper>
        <Table fixedLayout style={{ minWidth: "65rem" }} hasNest>
          <TableBody>
            {rows.map((row, rowIndex) => (
              <Fragment key={rowIndex}>
                <TableRow
                  selected={rowIndex === selectedRowIndex}
                  onClick={row.onClick}
                >
                  <TableRowHeader thValue={row.thValue} />
                  {row.items.map((item, itemIndex) =>
                    Object.keys(item).length > 0 ? (
                      <TableRowItem
                        key={itemIndex}
                        {...item}
                        title={item.formattedValue || ""}
                      />
                    ) : (
                      <Fragment key={itemIndex}></Fragment>
                    ),
                  )}
                </TableRow>

                {selectedRowIndex === rowIndex && (
                  <TableRow>
                    {/* + 1 is for the first column that is computed later */}
                    <TableNestingWrapper colSpan={row.items.length + 1}>
                      <Table nested fixedLayout style={{ minWidth: "65rem" }}>
                        <TableHeader>
                          <TableRow>
                            {row.nestedTable.th.map((th, thIndex) => (
                              <th style={th.style} key={thIndex}>
                                {th.value}
                              </th>
                            ))}
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {row.nestedTable.tr.map((tr, trIndex) => (
                            <TableRow key={trIndex} onClick={tr.onClick}>
                              {tr.td.map((td, tdIndex) => (
                                <td
                                  style={td.style}
                                  key={tdIndex}
                                  title={td.value}
                                >
                                  {td.value}
                                </td>
                              ))}
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableNestingWrapper>
                  </TableRow>
                )}
              </Fragment>
            ))}
          </TableBody>
        </Table>
      </TableWrapper>
    </Content>
  );
}

function ValueContainer({ children }) {
  return (
    <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
      {children}
    </div>
  );
}

function Pill({ children }) {
  return (
    <div
      style={{
        display: "inline-flex",
        lineHeight: "1",
        padding: "0.2rem",
        border: "var(--border)",
        borderRadius: "var(--borderRadiusSmall)",
        backgroundColor: "white",
        color: "var(--color-leano-1)",
        fontSize: "small",
      }}
    >
      {children}
    </div>
  );
}
